import { api } from '@goaltree/backend/convex/_generated/api'
import { inLineRegion } from '@goaltree/backend/convex/lib/lineRegion'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@goaltree/ui/components/field'
import { Input } from '@goaltree/ui/components/input'
import { cn } from '@goaltree/ui/lib/utils'
import { useForm } from '@tanstack/react-form'
import { useAction, useQuery } from 'convex/react'
import { Loader2, Mail } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { TURNSTILE_SITE_KEY, useTurnstile } from '@/hooks/use-turnstile'
import { type AuthSurface, trackAuthMethod, trackAuthScreen } from '@/lib/auth-analytics'
import { authClient } from '@/lib/auth-client'
import { getLastLogin, setLastLogin } from '@/lib/last-login'
import { pendingPlanReturnTo } from '@/store/onboarding.store'

interface LoginFormProps extends React.ComponentProps<'div'> {
  onComplete?: () => void
  onSwitchToSignUp?: () => void
  returnTo?: string
  signingIn?: boolean
  surface?: AuthSurface
  title?: string
  subtitle?: string
}

// Terms/Privacy live on the marketing site, not the app domain.
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'

const LineSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="size-5" aria-hidden>
    <path
      fill="#06C755"
      d="M24 10.3C24 4.6 18.6 0 12 0S0 4.6 0 10.3c0 5.1 4.3 9.4 10.1 10.2.4.1.9.3 1 .6.1.3.1.8 0 1.1l-.2 1c-.1.3-.2 1.2 1 .6 1.3-.5 7-4.1 9.5-7C23.1 15 24 12.8 24 10.3z"
    />
    <path
      fill="#fff"
      d="M8.2 12.8H5.6c-.2 0-.4-.2-.4-.4V8c0-.2.2-.4.4-.4s.4.2.4.4v4h2.2c.2 0 .4.2.4.4s-.2.4-.4.4zm1.8-.4c0 .2-.2.4-.4.4s-.4-.2-.4-.4V8c0-.2.2-.4.4-.4s.4.2.4.4v4.4zm4.8 0c0 .2-.1.3-.3.4h-.1c-.1 0-.2-.1-.3-.2l-2.3-3.1v2.9c0 .2-.2.4-.4.4s-.4-.2-.4-.4V8c0-.2.1-.3.3-.4h.1c.1 0 .2.1.3.2L14 10.9V8c0-.2.2-.4.4-.4s.4.2.4.4v4.4zm3.4-2.8c.2 0 .4.2.4.4s-.2.4-.4.4h-1.5v.9h1.5c.2 0 .4.2.4.4s-.2.4-.4.4h-1.9c-.2 0-.4-.2-.4-.4V8c0-.2.2-.4.4-.4h1.9c.2 0 .4.2.4.4s-.2.4-.4.4h-1.5v.9h1.5z"
    />
  </svg>
)

const GoogleSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="size-5">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

const AppStoreBadge = () => (
  <svg
    width="120"
    height="40"
    viewBox="0 0 120 40"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="120" height="40" rx="6" fill="#000" stroke="#555" strokeWidth="0.75" />
    <text x="37" y="13" fontFamily="sans-serif" fontSize="7" fill="#fff">
      Download on the
    </text>
    <text x="34" y="27" fontFamily="sans-serif" fontSize="13" fontWeight="bold" fill="#fff">
      App Store
    </text>
    <path
      d="M15 20.5c0-3.2 2.6-4.7 2.7-4.8-1.5-2.2-3.8-2.5-4.6-2.5-1.9-.2-3.8 1.1-4.7 1.1-1 0-2.5-1.1-4.1-1.1C2 13.2 0 14.5 0 17.9c0 4.9 4.3 12.1 6 12.1 1.6 0 2.2-1 4.1-1 1.9 0 2.4 1 4.2 1 1.7 0 2.8-1.6 3.9-3.1 1.2-1.8 1.7-3.5 1.7-3.6-.1 0-3.9-1.5-3.9-5.8z"
      transform="translate(10, 8) scale(0.65)"
      fill="#fff"
    />
    <path
      d="M12.5 9.5c.9-1.1 1.5-2.6 1.3-4.1-1.3.1-2.9.9-3.8 2C9.2 8.4 8.5 9.9 8.7 11.2c1.4.1 2.9-.7 3.8-1.7z"
      transform="translate(10, 8) scale(0.65)"
      fill="#fff"
    />
  </svg>
)

const PlayStoreBadge = () => (
  <svg
    width="135"
    height="40"
    viewBox="0 0 135 40"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="135" height="40" rx="6" fill="#000" stroke="#555" strokeWidth="0.75" />
    <text x="37" y="13" fontFamily="sans-serif" fontSize="7" fill="#fff">
      GET IT ON
    </text>
    <text x="34" y="27" fontFamily="sans-serif" fontSize="13" fontWeight="bold" fill="#fff">
      Google Play
    </text>
    <g transform="translate(9, 8) scale(0.9)">
      <path
        d="M1.2 0C.5.4 0 1.1 0 2v20c0 .9.5 1.7 1.2 2.1l.1.1 11.2-11.2v-.3L1.3 0z"
        fill="#4FC3F7"
      />
      <path
        d="M16.2 15.3l-3.7-3.7v-.3l3.7-3.7.1.1 4.4 2.5c1.3.7 1.3 1.9 0 2.6l-4.5 2.5z"
        fill="#FFCA28"
      />
      <path d="M16.3 15.2L12.5 11.4 1.2 22.1c.4.4 1.1.5 1.9.1l13.2-7z" fill="#F06292" />
      <path d="M16.3 7.6L3.1.5C2.3.1 1.6.2 1.2.6L12.5 11.4l3.8-3.8z" fill="#58CC02" />
    </g>
  </svg>
)

export function LoginForm({
  className,
  onComplete,
  onSwitchToSignUp: _unused,
  returnTo,
  signingIn = false,
  surface = 'login',
  title,
  subtitle,
  ...props
}: LoginFormProps) {
  const { t } = useTranslation()
  const providers = useQuery(api.auth.providers)
  const lineFirst = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      inLineRegion(
        navigator.languages ?? [navigator.language],
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      ),
    [],
  )
  // Someone who signed in with LINE before keeps the button when travelling.
  const showLine = !!providers?.line && (lineFirst || getLastLogin() === 'line')
  useEffect(() => trackAuthScreen(surface), [surface])
  const [sent, setSent] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [lineLoading, setLineLoading] = useState(false)
  const [sentEmail, setSentEmail] = useState('')
  // Installed PWAs sign in with an email code instead of a magic link (a link
  // opens the browser and the session never reaches the app).
  const isPwa =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true)
  const [lastLogin] = useState(getLastLogin)
  // Collapsed by default: a "Continue with email" CTA reveals the email input.
  const [showEmail, setShowEmail] = useState(lastLogin === 'email')
  const [otpMode, setOtpMode] = useState(false)
  const [otp, setOtp] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [otpError, setOtpError] = useState('')

  async function verifyOtp() {
    if (verifying || otp.length < 6) return
    setVerifying(true)
    setOtpError('')
    await authClient.signIn.emailOtp(
      { email: sentEmail, otp },
      {
        // Session is established in this (PWA) context; the /auth/login route's
        // isAuthenticated redirect forwards on — new users to onboarding.
        onSuccess: () => {
          setLastLogin('email')
          onComplete?.()
        },
        onError: (ctx) => {
          setOtpError(ctx.error.message || t('login.otpInvalid'))
          setVerifying(false)
        },
      },
    )
  }
  // A signed-out plan waiting to be saved routes sign-in back to its preview.
  const target = returnTo ?? pendingPlanReturnTo()
  // Magic-link opens in a fresh tab (no in-page state), so it lands on the
  // unprotected /auth/login, which waits for auth then forwards.
  const magicCallbackURL = target
    ? `/auth/login?returnTo=${encodeURIComponent(target)}`
    : '/auth/login'
  // Google returns in the same tab, where the oauthPending marker lets the
  // /today guard wait instead of bouncing — so we can land there directly.
  const socialCallbackURL = target ?? '/today'
  // Brand-new signups skip straight to onboarding (server knows they're new),
  // so they never route through /today and can't flash it — unless they were
  // heading somewhere specific (saving a plan, checkout), which then continues
  // into onboarding itself.
  const magicNewUserCallbackURL = target ? magicCallbackURL : '/goals/new'
  const socialNewUserCallbackURL = target ?? '/goals/new'
  const {
    containerRef: turnstileRef,
    token: turnstileToken,
    reset: resetTurnstile,
    waitForToken,
  } = useTurnstile()
  const verifyTurnstile = useAction(api.turnstile.verify)

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      trackAuthMethod('email', surface)
      // Verify Turnstile token if configured (skipped on localhost)
      if (TURNSTILE_SITE_KEY) {
        const token = turnstileToken ?? (await waitForToken(8000))
        if (!token) {
          toast.error(t('login.securityCheckPending'))
          return
        }
        const ok = await verifyTurnstile({ token }).catch(() => false)
        if (!ok) {
          toast.error(t('login.securityCheckFailed'))
          resetTurnstile()
          return
        }
      }
      // In the installed PWA a magic link can't complete (it opens the browser,
      // so the session lands there, not here). Send a code the user types back
      // into the app instead, which signs in within this context.
      if (isPwa) {
        await authClient.emailOtp.sendVerificationOtp(
          { email: value.email, type: 'sign-in' },
          {
            onSuccess: () => {
              setSentEmail(value.email)
              setOtpMode(true)
            },
            onError: (ctx) => {
              toast.error(ctx.error.message || ctx.error.statusText)
              resetTurnstile()
            },
          },
        )
        return
      }
      await authClient.signIn.magicLink(
        {
          email: value.email,
          callbackURL: magicCallbackURL,
          newUserCallbackURL: magicNewUserCallbackURL,
        },
        {
          onSuccess: () => {
            setLastLogin('email')
            setSentEmail(value.email)
            setSent(true)
          },
          onError: (ctx) => {
            toast.error(ctx.error.message || ctx.error.statusText)
            resetTurnstile()
          },
        },
      )
    },
    validators: {
      onSubmit: z.object({ email: z.email('Invalid email address') }),
    },
  })

  async function handleSocial(provider: 'google' | 'line') {
    const setLoading = provider === 'line' ? setLineLoading : setGoogleLoading
    setLoading(true)
    setLastLogin(provider)
    trackAuthMethod(provider, surface)
    // Marker survives the OAuth redirect round-trip so the button stays in its
    // loading state when the browser returns to /auth/login.
    sessionStorage.setItem('oauthPending', '1')
    await authClient.signIn.social(
      {
        provider,
        callbackURL: socialCallbackURL,
        newUserCallbackURL: socialNewUserCallbackURL,
      },
      {
        onSuccess: () => onComplete?.(),
        onError: (error) => {
          setLoading(false)
          sessionStorage.removeItem('oauthPending')
          toast.error(error.error.message || error.error.statusText)
        },
      },
    )
  }

  // Hitting back from Google's consent screen restores this page from the
  // browser's bfcache with googleLoading still true, freezing the spinner. Reset
  // it on bfcache restore and when the tab is shown again after the redirect.
  useEffect(() => {
    const clear = () => {
      setGoogleLoading(false)
      setLineLoading(false)
      sessionStorage.removeItem('oauthPending')
    }
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) clear()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') clear()
    }
    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  const googleBusy = googleLoading || signingIn
  const lineBusy = lineLoading || signingIn
  const anyBusy = googleBusy || lineBusy

  if (otpMode) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary-text" />
          </div>
          <h1 className="text-2xl font-bold">{t('login.enterCode')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('login.sentCode')} <span className="font-medium text-foreground">{sentEmail}</span>
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Input
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
              setOtpError('')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') verifyOtp()
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            autoFocus
            className="h-12 rounded-lg text-center text-lg tracking-[0.5em]"
          />
          {otpError && <p className="text-center text-xs text-destructive">{otpError}</p>}
          <button
            type="button"
            onClick={verifyOtp}
            disabled={verifying || otp.length < 6}
            className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('login.signingIn')}
              </>
            ) : (
              t('login.verifyCode')
            )}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setOtpMode(false)
            setOtp('')
            setOtpError('')
          }}
          className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {t('login.differentEmail')}
        </button>
      </div>
    )
  }

  if (sent) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary-text" />
          </div>
          <h1 className="text-2xl font-bold">{t('login.checkInbox')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('login.sentLink')} <span className="font-medium text-foreground">{sentEmail}</span>
          </p>
          <p className="text-xs text-muted-foreground">{t('login.linkExpires')}</p>
        </div>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {t('login.differentEmail')}
        </button>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <div className="flex flex-col items-center gap-1 text-center">
        {/* In a dialog, clear of its close button at the top right. */}
        <h1 className={cn('text-3xl font-bold text-balance', surface === 'modal' && 'px-9')}>
          {title ?? t('login.welcome')}
        </h1>
        <p className="text-sm text-muted-foreground">{subtitle ?? t('login.subtitle')}</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <FieldGroup>
          <Field className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => handleSocial('google')}
              disabled={anyBusy}
              className="relative flex h-14 w-full cursor-pointer items-center justify-center gap-3 rounded-2xl border border-black/10 bg-white text-sm font-semibold text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 disabled:cursor-default disabled:opacity-80"
            >
              {lastLogin === 'google' && !googleBusy && <LastUsedPill />}
              {googleBusy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('login.signingIn')}
                </>
              ) : (
                <>
                  <GoogleSvg />
                  Continue with Google
                </>
              )}
            </button>
            {showLine && (
              <button
                type="button"
                onClick={() => handleSocial('line')}
                disabled={anyBusy}
                className={cn(
                  'relative flex h-14 w-full cursor-pointer items-center justify-center gap-3 rounded-2xl border border-black/10 bg-white text-sm font-semibold text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100 disabled:cursor-default disabled:opacity-80',
                  lineFirst && 'order-first',
                )}
              >
                {lastLogin === 'line' && !lineBusy && <LastUsedPill />}
                {lineBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  <>
                    <LineSvg />
                    {t('login.continueWithLine')}
                  </>
                )}
              </button>
            )}
            {showLine && (
              <p className="order-last text-center text-xs text-muted-foreground">
                {t('login.lineEmailNotice')}
              </p>
            )}
          </Field>

          {showEmail ? (
            <>
              <form.Field name="email">
                {(field) => (
                  <Field data-invalid={field.state.meta.errors.length > 0 || undefined}>
                    <FieldLabel className="flex items-center gap-2 text-sm" htmlFor={field.name}>
                      {t('login.emailLabel')}
                      {lastLogin === 'email' && <LastUsedPill inline />}
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      // biome-ignore lint/a11y/noAutofocus: focus the revealed email input
                      autoFocus
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      className="h-11 rounded-lg px-4 text-sm"
                      autoComplete="email"
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </Field>
                )}
              </form.Field>

              <form.Subscribe
                selector={(state) => ({
                  canSubmit: state.canSubmit,
                  isSubmitting: state.isSubmitting,
                })}
              >
                {({ canSubmit, isSubmitting }) => (
                  <Field>
                    <button
                      type="submit"
                      disabled={!canSubmit || isSubmitting}
                      className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('login.sendingLink')}
                        </>
                      ) : (
                        t('login.continue')
                      )}
                    </button>
                  </Field>
                )}
              </form.Subscribe>
            </>
          ) : (
            <Field>
              <button
                type="button"
                onClick={() => setShowEmail(true)}
                className="relative flex h-14 w-full cursor-pointer items-center justify-center gap-3 rounded-2xl bg-foreground/[0.06] text-sm font-medium text-foreground transition-colors hover:bg-foreground/10"
              >
                {lastLogin === 'email' && <LastUsedPill />}
                <Mail className="h-4 w-4" />
                {t('login.continueWithEmail')}
              </button>
            </Field>
          )}
        </FieldGroup>
      </form>

      {/* Turnstile: invisible unless Cloudflare asks the visitor to tick a box */}
      <div ref={turnstileRef} className="flex justify-center empty:hidden" />

      <FieldDescription className="text-center">
        {t('login.terms')}{' '}
        <a
          href={`${SITE_URL}/terms`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-primary-text"
        >
          {t('login.termsLink')}
        </a>{' '}
        {t('common.and')}{' '}
        <a
          href={`${SITE_URL}/privacy`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-primary-text"
        >
          {t('login.privacyLink')}
        </a>
        .
      </FieldDescription>

      <div className="flex flex-col items-center gap-3">
        <p className="text-xs text-muted-foreground">{t('login.availableOn')}</p>
        <div className="flex items-center gap-3">
          <a
            href="https://goaltree.ai/waitlist"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-60 transition-opacity hover:opacity-100"
          >
            <AppStoreBadge />
          </a>
          <a
            href="https://goaltree.ai/waitlist"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-60 transition-opacity hover:opacity-100"
          >
            <PlayStoreBadge />
          </a>
        </div>
      </div>
    </div>
  )
}

function LastUsedPill({ inline = false }: { inline?: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground',
        !inline && 'absolute -top-2 right-3 shadow-sm',
      )}
    >
      {t('login.lastUsed')}
    </span>
  )
}
